import React, { FormEvent, useEffect, useState } from 'react';

import { observer } from 'mobx-react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Alert from '../Alert';
import EmailInput from '../EmailInput';

import './Signup.css';

import { FormattedMessage } from 'react-intl';

import { trackBingEvent } from 'src/analytics/bing';
import { trackDataLayerEvent, trackGoogleSignup } from 'src/analytics/google';
import { useISIntl } from 'src/i18n';
import BillingModel from 'src/models/BillingModel';
import UserModel from 'src/models/UserModel';
import { useOptimizelyFlag } from 'src/services/flags/hooks/useOptimizelyFlag';
import { AppStore } from 'src/stores/AppStore';
import { SubscriptionTrackerStore } from 'src/subs';
import { BillingAutoComplete } from '../BillingAutoComplete';
import { messages } from './messages';
import { handleConsent } from './utils';

type SignupV1Props = {
  store: AppStore;
  postSignupCallback?: Function;
};

export const SignupV1 = observer(({ store, postSignupCallback }: SignupV1Props) => {
  const [consent, setConsent] = useState(false);

  const { f } = useISIntl();

  const user = UserModel.getInstance();
  const billing = BillingModel.getInstance();
  const data = user.signupData;

  const isFreeTrialEnabled = useOptimizelyFlag('web_trial7day').enabled;
  const isRegistrationBillingRequired = useOptimizelyFlag('registration_billing').enabled;

  useEffect(() => {
    const email = new URLSearchParams(window.location.search).get('email');

    if (user && email) {
      user.signupData.email = email;
    }
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const { email, password, firstName, lastName } = data;
    user.signupData = { email, password, firstName, lastName };

    const signUpResult = await user.signup({
      flags: {
        isFreeTrialEnabled,
        isRegistrationBillingRequired: isRegistrationBillingRequired
      }
    });

    if (!signUpResult) {
      return;
    }

    trackGoogleSignup();
    trackBingEvent('signup');
    trackDataLayerEvent('signup', {
      user_id: user.id
    });

    await handleConsent(consent);

    if (postSignupCallback) {
      postSignupCallback();
    }
  };

  const handleNav = (name: string, e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();

    if (name === 'home') {
      store.nav('invoiceList');
      return;
    }

    store.nav(name, undefined, true);
  };

  const isBillingEmpty = () => {
    if (isRegistrationBillingRequired) {
      return !billing.isValid.validity;
    }
    return false;
  };

  return (
    <div className="signup">
      <div className="focus-container">
        <div className="focus-header">
          <a className="brand" href="/" onClick={(e) => handleNav('home', e)}>
            <img
              className="brand-logo"
              src="/images/logo.png"
              role="presentation"
              alt="invoice simple logo"
              data-pin-nopin="true"
            />
          </a>
          <h1>
            <FormattedMessage {...messages.header} />
          </h1>
          <h2>
            <FormattedMessage {...messages.subHeader} />
          </h2>
        </div>
        <div className="focus-body">
          {SubscriptionTrackerStore.isVerifying ? (
            <div className="alert-loading d-flex justify-content-center pb-4">
              <FontAwesomeIcon icon="spinner" size="lg" color="black" spin />
            </div>
          ) : (
            <Alert />
          )}
          <form acceptCharset="UTF-8" onSubmit={(e) => onSubmit(e)}>
            <div className="form-group">
              <label htmlFor="firstName">
                <FormattedMessage {...messages.formLabelFirstName} />
              </label>
              <input
                id="firstName"
                type="text"
                placeholder={f(messages.formPlaceholderFirstName)}
                disabled={user.isSigningUp}
                value={data.firstName}
                onChange={(e) => (data.firstName = e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">
                <FormattedMessage {...messages.formLabelLastName} />
              </label>
              <input
                id="lastName"
                type="text"
                placeholder={f(messages.formPlaceholderLastName)}
                disabled={user.isSigningUp}
                value={data.lastName}
                onChange={(e) => (data.lastName = e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">
                <FormattedMessage {...messages.formLabelEmail} />
              </label>
              <EmailInput
                key="user-signup"
                name="email"
                placeholder={
                  SubscriptionTrackerStore.isVerifying
                    ? f(messages.formButtonLabelLoading)
                    : f(messages.formPlaceholderEmail)
                }
                disabled={SubscriptionTrackerStore.isVerifying || user.isSigningUp}
                value={data.email}
                onChange={(e) => (data.email = e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">
                <FormattedMessage {...messages.formLabelPassword} />
              </label>
              <input
                id="password"
                type="password"
                placeholder="******"
                disabled={user.isSigningUp}
                value={data.password}
                onChange={(e) => (data.password = e.target.value)}
              />
            </div>
            {isRegistrationBillingRequired && (
              <BillingAutoComplete
                label={f(messages.formLabelAddress)}
                placeholder={f(messages.formPlaceholderAddress)}
                disabled={user.isSigningUp}
                inputId="address"
                hideDropdownIndicator
              />
            )}
            <div id="showmore" className="consent-management">
              <div className="consent-checkbox">
                <input
                  id="consent"
                  type="checkbox"
                  data-testid="consent-checkbox"
                  checked={consent}
                  onChange={(e) => {
                    setConsent(e.target.checked);
                  }}
                  disabled={user.isSigningUp}
                />
              </div>
              <div>
                <p>
                  <FormattedMessage {...messages.consentVisible} />
                  <span className="showmore-content" data-testid="showmore-content">
                    {' '}
                    <FormattedMessage {...messages.consentHidden} />
                  </span>
                  <span className="showmore-trigger">
                    ...{' '}
                    <a href="#showmore" className="showmore-link">
                      <FormattedMessage {...messages.consentShowMore} />
                    </a>
                  </span>
                </p>
              </div>
            </div>
            <div className="form-group">
              <button
                type="submit"
                className="btn btn-block btn-prime"
                data-testid="signup-btn"
                disabled={
                  SubscriptionTrackerStore.isVerifying || user.isSigningUp || isBillingEmpty()
                }>
                {user.isSigningUp ? (
                  <FormattedMessage {...messages.formButtonLabelPending} />
                ) : user.isSigningUp ? (
                  <FormattedMessage {...messages.formButtonLabelLoading} />
                ) : (
                  <FormattedMessage {...messages.formButtonLabel} />
                )}
              </button>
              <a className="btn btn-cancel" onClick={(e) => handleNav('home', e)}>
                <FormattedMessage {...messages.cancelButtonLabel} />
              </a>
              <div className="signup-terms">
                <FormattedMessage {...messages.signupTermsAgreement} />
              </div>
            </div>
          </form>
        </div>
        <div className="focus-footer">
          <FormattedMessage {...messages.loginLinkQuestion} />
          <a
            href="/login"
            className="btn"
            onClick={(e) => handleNav('login', e)}
            style={{ marginLeft: 10 }}>
            <FormattedMessage {...messages.loginLinkLabel} />
          </a>
        </div>
      </div>
    </div>
  );
});
